// Guided Report Builder (reporting foundation R4) — a 3-step flow over the
// server-side report engine: pick a data area (domains are already
// permission-filtered per viewer), pick columns + filters (fields too), then
// preview (row-capped at 200), export CSV or save as a definition. Saved
// builder reports carry NO template_id — the saved viewer runs them through
// the engine under the viewer's own permissions.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Switch } from '@/features/shared/components/ui/switch';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/features/shared/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/features/shared/components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Download,
  FileBarChart2,
  Loader2,
  Play,
  Plus,
  Save,
  ShieldAlert,
  X,
} from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { useHasPermission } from '@/rbac/useRbac';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { toCSV, downloadCsv } from '@/lib/csv';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import type { PayrollRun } from '@/features/payroll/types';
import { createSavedReport, type ReportDomain } from '@/services/savedReportService';
import {
  listReportDomains,
  getDomainFields,
  previewReport,
  exportReport,
  openReportDownload,
  formatCell,
  isNumericFieldType,
  type ReportDomainSummary,
  type ReportDomainFields,
  type ReportEngineResult,
  type ReportExportFormat,
} from '@/services/reportEngineService';
import { EngineFilterBar, cleanFilters } from './EngineFilterBar';
import { ReportScopeNotice, ReportScopeEmptyReason } from './ReportScopeNotice';
import { EngineResultChart } from './EngineResultChart';
import { useUnsavedGuard, usePendingGuard } from '@/features/shared/components/recovery/useFormGuards';

// ---- Aggregations (R6) ------------------------------------------------------------

type AggFn = 'count' | 'sum' | 'avg' | 'min' | 'max';
interface AggRow {
  fn: AggFn;
  /** Numeric field key — unused (and disabled) for 'count'. */
  field: string;
}

const AGG_FN_LABELS: Record<AggFn, string> = {
  count: 'Count',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
};

/** Wire encoding: 'count' | 'sum:<field>' | 'avg:<field>' | 'min:<field>' | 'max:<field>'. */
const encodeAggregations = (rows: AggRow[]): string[] =>
  rows
    .filter((r) => r.fn === 'count' || r.field)
    .map((r) => (r.fn === 'count' ? 'count' : `${r.fn}:${r.field}`));

const DEFAULT_COLUMNS = 6;

const apiErrorMessage = (e: any, fallback: string): string => getApiErrorMessage(e, fallback);

// ---- Stepper (matches the BulkImportPage wizard feel) ----------------------------

const Stepper: React.FC<{ current: number; labels: string[] }> = ({ current, labels }) => (
  <div className="flex items-center gap-2">
    {labels.map((label, i) => (
      <React.Fragment key={label}>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
              i <= current ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {i < current ? '✓' : i + 1}
          </span>
          <span
            className={`text-sm ${i === current ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
          >
            {label}
          </span>
        </div>
        {i < labels.length - 1 && <div className="flex-1 h-px bg-border min-w-[16px]" />}
      </React.Fragment>
    ))}
  </div>
);

// ---- Page ------------------------------------------------------------------------

export const ReportBuilderPage: React.FC = () => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const canCreate = useHasPermission('reports:create');
  const canExport = useHasPermission('reports:export');
  const { isTenantOwner } = useRoleAccess();

  // .../reports/builder → reports base for navigation.
  const base = pathname.slice(0, pathname.indexOf('/reports') + '/reports'.length);

  const [step, setStep] = useState(0);

  // ---- Step 1: domains --------------------------------------------------------
  const [domains, setDomains] = useState<ReportDomainSummary[] | null>(null);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [domainsError, setDomainsError] = useState('');
  const [domainsForbidden, setDomainsForbidden] = useState(false);

  useEffect(() => {
    let active = true;
    listReportDomains()
      .then((list) => active && setDomains(list))
      .catch((e: any) => {
        if (!active) return;
        if (e?.response?.status === 403) setDomainsForbidden(true);
        else setDomainsError(apiErrorMessage(e, t('builder.domainsFailed', 'Could not load the available data areas.')));
      })
      .finally(() => active && setDomainsLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Step 2: fields + filters -------------------------------------------------
  const [domainKey, setDomainKey] = useState('');
  const [fieldsMeta, setFieldsMeta] = useState<ReportDomainFields | null>(null);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldsError, setFieldsError] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const chooseDomain = (d: ReportDomainSummary) => {
    setDomainKey(d.key);
    setFieldsMeta(null);
    setFieldsError('');
    setSelectedKeys([]);
    setFilterValues({});
    setGroupBy([]);
    setAggRows([]);
    setResult(null);
    setPreviewError('');
    setFieldsLoading(true);
    setStep(1);
    getDomainFields(d.key)
      .then((raw) => {
        // Normalize once: a domain with no filters/fields (e.g. Leave Balances)
        // can return null arrays — coerce to [] so every downstream access
        // (.length/.slice/.map/.filter) is safe.
        const meta = {
          ...raw,
          fields: Array.isArray(raw?.fields) ? raw.fields : [],
          filters: Array.isArray(raw?.filters) ? raw.filters : [],
        } as ReportDomainFields;
        setFieldsMeta(meta);
        // Default selection: the first 6 fields the viewer may see.
        setSelectedKeys(meta.fields.slice(0, DEFAULT_COLUMNS).map((f) => f.key));
      })
      .catch((e: any) => {
        setFieldsError(
          e?.response?.status === 403
            ? t('builder.fieldsForbidden', 'You do not have access to this data area.')
            : apiErrorMessage(e, t('builder.fieldsFailed', 'Could not load the fields for this data area.')),
        );
      })
      .finally(() => setFieldsLoading(false));
  };

  // Payroll-run picker data: load runs only when the domain declares a `run`
  // filter; default to the most recent non-draft run (drafts have no snapshot).
  const runFilterKey = useMemo(
    () => fieldsMeta?.filters?.find((f) => f.type === 'run')?.key ?? '',
    [fieldsMeta],
  );

  useEffect(() => {
    if (!runFilterKey) return;
    let active = true;
    setRunsLoading(true);
    payrollApi
      .listRuns()
      .then((list) => {
        if (!active) return;
        const nonDraft = list.filter((r) => r.state !== 'draft');
        setRuns(nonDraft);
        if (nonDraft[0]) {
          setFilterValues((cur) => (cur[runFilterKey] ? cur : { ...cur, [runFilterKey]: nonDraft[0].id }));
        }
      })
      .catch(() => active && setRuns([]))
      .finally(() => active && setRunsLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runFilterKey]);

  const toggleField = (key: string) => {
    setSelectedKeys((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };

  const setFilter = (key: string, value: string) =>
    setFilterValues((cur) => ({ ...cur, [key]: value }));

  // Selected columns in the server's field order (stable, readable tables).
  const orderedSelection = useMemo(
    () => (fieldsMeta?.fields ? fieldsMeta.fields.filter((f) => selectedKeys.includes(f.key)).map((f) => f.key) : []),
    [fieldsMeta, selectedKeys],
  );

  // ---- Summarize (R6): optional group-by + aggregations --------------------------
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [aggRows, setAggRows] = useState<AggRow[]>([]);

  const groupableFields = useMemo(
    () => fieldsMeta?.fields?.filter((f) => f.groupable) ?? [],
    [fieldsMeta],
  );
  const numericFields = useMemo(
    () => fieldsMeta?.fields?.filter((f) => isNumericFieldType(f.type)) ?? [],
    [fieldsMeta],
  );

  const encodedAggs = useMemo(() => encodeAggregations(aggRows), [aggRows]);
  const summarizing = groupBy.length > 0 || encodedAggs.length > 0;

  const toggleGroup = (key: string) =>
    setGroupBy((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));

  const addAggRow = () => setAggRows((cur) => [...cur, { fn: 'count', field: '' }]);
  const removeAggRow = (i: number) => setAggRows((cur) => cur.filter((_, idx) => idx !== i));
  const patchAggRow = (i: number, patch: Partial<AggRow>) =>
    setAggRows((cur) => cur.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  // ---- Step 3: preview -----------------------------------------------------------
  const [result, setResult] = useState<ReportEngineResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const runPreview = () => {
    if (!fieldsMeta || orderedSelection.length === 0 || previewLoading) return;
    setPreviewLoading(true);
    setPreviewError('');
    previewReport({
      domain: fieldsMeta.domain,
      columns: orderedSelection,
      filters: cleanFilters(filterValues),
      ...(summarizing ? { group_by: groupBy, aggregations: encodedAggs } : {}),
    })
      .then((res) => setResult(res))
      .catch((e: any) => {
        setResult(null);
        const msg = apiErrorMessage(e, t('builder.previewFailed', 'Could not run the preview.'));
        setPreviewError(msg);
        toast.error(msg);
      })
      .finally(() => setPreviewLoading(false));
  };

  const goToPreview = () => {
    setStep(2);
    setResult(null);
    setPreviewError('');
  };

  // Auto-run the preview when entering step 3 with a valid selection.
  useEffect(() => {
    if (step === 2 && !result && !previewLoading && !previewError && orderedSelection.length > 0) {
      runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ---- CSV export --------------------------------------------------------------

  const domainLabel = fieldsMeta?.label ?? domains?.find((d) => d.key === domainKey)?.label ?? domainKey;

  const filterSummary = (): string => {
    const clean = cleanFilters(filterValues);
    const parts = Object.entries(clean).map(([k, v]) => {
      if (k === runFilterKey && runFilterKey) {
        const run = runs.find((r) => r.id === v);
        return `run=${run ? `${run.periodLabel} (${run.state})` : v}`;
      }
      return `${k}=${v}`;
    });
    return parts.length ? parts.join('; ') : 'none';
  };

  const exportCsv = () => {
    if (!result) return;
    const generatedBy = user ? `${user.name || ''}${user.email ? ` <${user.email}>` : ''}`.trim() : 'unknown';
    const meta: (string | number | null)[][] = [
      ['Report', domainLabel],
      ['Generated at', new Date().toISOString()],
      ['Generated by', generatedBy],
      ['Filters used', filterSummary()],
      ['Source', result.source === 'snapshot' ? 'locked payroll snapshot' : 'live data'],
    ];
    const headers = result.columns.map((c) => c.label);
    const rows = result.rows.map((row) => result.columns.map((c) => formatCell(row[c.key], c.type)));
    const csv = `${toCSV([], meta)}\r\n\r\n${toCSV(headers, rows)}`;
    downloadCsv(`${domainKey}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  // ---- Server export (R5): full file (metadata block included) built server-side.
  const [exporting, setExporting] = useState(false);

  const serverExport = async (format: ReportExportFormat) => {
    if (!fieldsMeta || exporting || orderedSelection.length === 0) return;
    setExporting(true);
    try {
      const res = await exportReport({
        domain: fieldsMeta.domain,
        columns: orderedSelection,
        filters: cleanFilters(filterValues),
        ...(summarizing ? { group_by: groupBy, aggregations: encodedAggs } : {}),
        format,
        title: domainLabel,
      });
      openReportDownload(res.download_url);
      let msg = t('export.ready', 'Export ready — {{count}} row(s).', { count: res.row_count });
      if (res.truncated) msg += ` ${t('export.truncatedNote', 'Truncated at 5000 rows.')}`;
      if (res.confidential)
        msg += ` ${t('export.confidentialNote', 'Contains confidential data — handle accordingly.')}`;
      toast.success(msg, { duration: 6000 });
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('export.failed', 'Could not export this report.'))));
    } finally {
      setExporting(false);
    }
  };

  // ---- Save dialog ---------------------------------------------------------------

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [publishTenant, setPublishTenant] = useState(false);
  const [saving, setSaving] = useState(false);
  // The typed part of saving a report: a name and description that exist
  // nowhere else yet. The COMPOSED definition (columns, grouping, filters) is
  // deliberately not treated as unsaved — it is exploratory and reproducible
  // from the same screen, and warning on every exit from the builder would be
  // noise that teaches people to dismiss the dialog. Recorded in
  // docs/pwa/05-guard-coverage.md rather than left implicit.
  useUnsavedGuard(saveOpen && (saveName.trim() !== '' || saveDescription.trim() !== ''),
    'report-builder', 'a report name and description');
  usePendingGuard(saving, 'report-builder', 'a report definition');

  const submitSave = async () => {
    const name = saveName.trim();
    if (!name || saving || !fieldsMeta) return;
    setSaving(true);
    try {
      // Builder reports carry NO template_id — the saved viewer executes them
      // through the engine (runSavedReport) under the viewer's permissions.
      const created = await createSavedReport({
        name,
        description: saveDescription.trim() || undefined,
        domain: fieldsMeta.domain as ReportDomain,
        columns: orderedSelection,
        filters: cleanFilters(filterValues),
        ...(summarizing ? { group_by: groupBy, aggregations: encodedAggs } : {}),
        visibility: publishTenant ? 'tenant' : 'private',
      });
      setSaveOpen(false);
      toast.success(t('saved.created', 'Report saved. Find it under "My reports".'));
      navigate(`${base}/saved/${created.id}`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, apiErrorMessage(e, t('saved.createFailed', 'Could not save this report.'))));
    } finally {
      setSaving(false);
    }
  };

  // ---- Render ---------------------------------------------------------------------

  const stepLabels = [
    t('builder.steps.area', 'Choose data area'),
    t('builder.steps.columns', 'Columns & filters'),
    t('builder.steps.preview', 'Preview & save'),
  ];

  const restrictedHint = t(
    'builder.restrictedHint',
    'Restricted field — visible to you because of your role',
  );

  const standardFields = fieldsMeta?.fields?.filter((f) => f.sensitivity === 'INTERNAL') ?? [];
  const restrictedFields = fieldsMeta?.fields?.filter((f) => f.sensitivity !== 'INTERNAL') ?? [];

  const renderFieldCheckbox = (f: (typeof standardFields)[number]) => (
    <label
      key={f.key}
      className="flex items-start gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:border-orange-400 transition-colors"
    >
      <Checkbox
        checked={selectedKeys.includes(f.key)}
        onCheckedChange={() => toggleField(f.key)}
        className="mt-0.5"
      />
      <span className="flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-foreground">{f.label}</span>
          {f.sensitivity !== 'INTERNAL' && (
            <span
              title={restrictedHint}
              className="inline-block h-2 w-2 rounded-full bg-warning shrink-0"
              aria-label={restrictedHint}
            />
          )}
        </span>
        {f.sensitivity !== 'INTERNAL' && (
          <span className="block text-xs text-warning mt-0.5">{restrictedHint}</span>
        )}
      </span>
    </label>
  );

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(base)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('viewer.back', 'Back to reports')}
        </button>
        <h1 className="text-2xl font-bold text-foreground mt-2">{t('builder.title', 'New report')}</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          {t(
            'builder.subtitle',
            'Build a custom report step by step — pick a data area, choose columns and filters, then preview and save.',
          )}
        </p>
      </div>

      <Stepper current={step} labels={stepLabels} />

      {/* ---- Step 1: choose data area ------------------------------------------- */}
      {step === 0 && (
        <div className="space-y-4">
          {domainsLoading ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('builder.loadingDomains', 'Loading data areas…')}
            </div>
          ) : domainsForbidden ? (
            <EmptyState
              title={t('builder.noAccessTitle', 'Reports are not available for your role')}
              body={t(
                'builder.noAccessBody',
                'Building reports requires a reports role. If you believe you should have access, ask your administrator.',
              )}
            />
          ) : domainsError ? (
            <ErrorBox message={domainsError} />
          ) : !domains || domains.length === 0 ? (
            <EmptyState
              title={t('builder.noDomainsTitle', 'No data areas available')}
              body={t('builder.noDomainsBody', 'There is nothing your role can report on yet.')}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {domains.map((d) => (
                <Card
                  key={d.key}
                  className={`cursor-pointer transition-colors hover:border-orange-300 ${
                    domainKey === d.key ? 'border-orange-400 ring-1 ring-orange-200' : ''
                  }`}
                  onClick={() => chooseDomain(d)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base leading-snug">{d.label}</CardTitle>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 ${
                          d.source === 'snapshot'
                            ? 'bg-sky-50 text-sky-700 border-sky-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}
                      >
                        {d.source === 'snapshot'
                          ? t('kind.snapshot', 'Payroll snapshot')
                          : t('kind.live', 'Live data')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-1">
                    <p className="text-sm text-muted-foreground">{d.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- Step 2: columns & filters ------------------------------------------- */}
      {step === 1 && (
        <div className="space-y-4">
          {fieldsLoading ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              {t('builder.loadingFields', 'Loading fields…')}
            </div>
          ) : fieldsError ? (
            <>
              <ErrorBox message={fieldsError} />
              <Button variant="outline" onClick={() => setStep(0)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('builder.backToAreas', 'Back to data areas')}
              </Button>
            </>
          ) : fieldsMeta ? (
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    {t('builder.columnsTitle', 'Columns — {{domain}}', { domain: fieldsMeta.label })}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {t('builder.columnsHint', 'Pick the columns your report should include.')}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      {t('builder.standardFields', 'Standard fields')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {standardFields.map(renderFieldCheckbox)}
                    </div>
                  </div>
                  {restrictedFields.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-warning uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <ShieldAlert className="w-3.5 h-3.5" />
                        {t('builder.restrictedFields', 'Restricted fields')}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {restrictedFields.map(renderFieldCheckbox)}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {fieldsMeta.filters.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{t('builder.filtersTitle', 'Filters')}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {t('builder.filtersHint', 'Optional — narrow the rows before previewing.')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <EngineFilterBar
                      filters={fieldsMeta.filters}
                      values={filterValues}
                      onChange={setFilter}
                      runs={runs}
                      runsLoading={runsLoading}
                    />
                  </CardContent>
                </Card>
              )}

              {/* ---- Summarize (R6): optional group-by + aggregations ---------------- */}
              {(groupableFields.length > 0 || numericFields.length > 0) && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      {t('builder.summarizeTitle', 'Summarize')}{' '}
                      <span className="text-muted-foreground font-normal text-sm">
                        {t('optional', '(optional)')}
                      </span>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'builder.summarizeHint',
                        'Group rows and compute totals instead of listing every row.',
                      )}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {groupableFields.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                          {t('builder.groupBy', 'Group by')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {groupableFields.map((f) => {
                            const active = groupBy.includes(f.key);
                            return (
                              <button
                                key={f.key}
                                type="button"
                                onClick={() => toggleGroup(f.key)}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                                  active
                                    ? 'border-orange-400 bg-orange-50 text-orange-700'
                                    : 'border-slate-200 bg-card text-slate-700 hover:border-slate-300'
                                }`}
                              >
                                {f.label}
                                {active && <X className="w-3 h-3" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        {t('builder.aggregations', 'Aggregations')}
                      </p>
                      {aggRows.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {aggRows.map((row, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-2">
                              <Select
                                value={row.fn}
                                onValueChange={(v) => patchAggRow(i, { fn: v as AggFn })}
                              >
                                <SelectTrigger className="w-36">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {(Object.keys(AGG_FN_LABELS) as AggFn[]).map((fn) => (
                                    <SelectItem key={fn} value={fn}>
                                      {t(`builder.aggFn.${fn}`, AGG_FN_LABELS[fn])}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={row.field || undefined}
                                onValueChange={(v) => patchAggRow(i, { field: v })}
                                disabled={row.fn === 'count' || numericFields.length === 0}
                              >
                                <SelectTrigger className="w-56">
                                  <SelectValue
                                    placeholder={
                                      row.fn === 'count'
                                        ? t('builder.aggAllRows', 'All rows')
                                        : t('builder.aggPickField', 'Pick a numeric field…')
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {numericFields.map((f) => (
                                    <SelectItem key={f.key} value={f.key}>
                                      {f.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={t('builder.removeAggregation', 'Remove aggregation')}
                                onClick={() => removeAggRow(i)}
                                className="text-muted-foreground hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Button variant="outline" size="sm" onClick={addAggRow}>
                        <Plus className="w-4 h-4 mr-2" />
                        {t('builder.addAggregation', 'Add aggregation')}
                      </Button>
                      {summarizing && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {t(
                            'builder.summarizeActive',
                            'The preview will show one row per group with the computed values.',
                          )}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep(0)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('back', 'Back')}
                </Button>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    {t('builder.selectedCount', '{{count}} column(s) selected', {
                      count: orderedSelection.length,
                    })}
                  </span>
                  <Button onClick={goToPreview} disabled={orderedSelection.length === 0}>
                    {t('builder.continue', 'Continue')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ---- Step 3: preview & save ------------------------------------------------ */}
      {step === 2 && fieldsMeta && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[11px]">
                {fieldsMeta.source === 'snapshot'
                  ? t('viewer.sourceSnapshot', 'Source: locked payroll snapshot')
                  : t('viewer.sourceLive', 'Source: live data')}
              </Badge>
              <span className="text-sm text-muted-foreground">{domainLabel}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={runPreview} disabled={previewLoading}>
                {previewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {t('builder.preview', 'Preview')}
              </Button>
              <Button variant="outline" onClick={exportCsv} disabled={!result || result.rows.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                {t('export.previewCsv', 'CSV (preview rows)')}
              </Button>
              {canExport && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" disabled={exporting || orderedSelection.length === 0}>
                      {exporting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      {t('export.export', 'Export')}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => serverExport('xlsx')}>
                      {t('export.xlsx', 'Excel (.xlsx)')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => serverExport('csv')}>
                      {t('export.csvFull', 'CSV (full)')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {canCreate && (
                <Button
                  onClick={() => {
                    setSaveName(domainLabel);
                    setSaveDescription('');
                    setPublishTenant(false);
                    setSaveOpen(true);
                  }}
                  disabled={orderedSelection.length === 0}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {t('saved.saveReport', 'Save report')}
                </Button>
              )}
            </div>
          </div>

          {previewError && <ErrorBox message={previewError} />}

          <Card>
            <CardContent className="pt-6">
              {previewLoading ? (
                <div className="py-16 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {t('viewer.loading', 'Running report…')}
                </div>
              ) : !result ? (
                <div className="py-16 text-center text-muted-foreground">
                  <FileBarChart2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  {previewError
                    ? t('builder.previewErrorHint', 'Fix the problem above and preview again.')
                    : t('builder.previewPrompt', 'Run the preview to see your report.')}
                </div>
              ) : result.rows.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                  <FileBarChart2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  {t('viewer.empty', 'No rows for the selected filters.')}
                  <ReportScopeEmptyReason scope={result.scope} />
                </div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground mb-3">
                    {t('viewer.rowCount', '{{count}} row(s)', { count: result.rows.length })}
                    {result.truncated && (
                      <span className="ml-2 text-warning">
                        {t('builder.truncated', '— showing first 200 of {{total}}', { total: result.total })}
                      </span>
                    )}
                    <ReportScopeNotice scope={result.scope} />
                  </div>
                  {summarizing && <EngineResultChart result={result} groupBy={groupBy} />}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {result.columns.map((c) => (
                            <TableHead key={c.key}>{c.label}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.rows.map((row, i) => (
                          <TableRow key={i}>
                            {result.columns.map((c) => (
                              <TableCell key={c.key}>{formatCell(row[c.key], c.type)}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-start">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('builder.backToColumns', 'Back to columns & filters')}
            </Button>
          </div>
        </div>
      )}

      {/* ---- Save dialog ------------------------------------------------------------ */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('saved.dialogTitle', 'Save report')}</DialogTitle>
            <DialogDescription>
              {t(
                'builder.saveDialogBody',
                'Saves this report with its columns and filters so you can re-run it from "My reports".',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="builder-report-name">{t('saved.name', 'Name')}</Label>
              <Input
                id="builder-report-name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder={t('saved.namePlaceholder', 'e.g. Monthly new hires — HQ')}
                maxLength={160}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="builder-report-description">
                {t('saved.description', 'Description')}{' '}
                <span className="text-muted-foreground font-normal">{t('optional', '(optional)')}</span>
              </Label>
              <Textarea
                id="builder-report-description"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                rows={3}
                placeholder={t('saved.descriptionPlaceholder', 'What this report is for…')}
              />
            </div>
            {isTenantOwner && (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="pr-3">
                  <p className="text-sm font-medium text-foreground">
                    {t('saved.publishTenant', 'Publish to whole company')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('saved.publishTenantHint', 'Everyone with reports access sees it; otherwise it stays private to you.')}
                  </p>
                </div>
                <Switch checked={publishTenant} onCheckedChange={setPublishTenant} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={saving}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button onClick={submitSave} disabled={!saveName.trim() || saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('saved.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- Small states ----------------------------------------------------------------

const ErrorBox: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-start gap-2 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger-strong">
    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
    <span>{message}</span>
  </div>
);

const EmptyState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="text-center py-16">
    <FileBarChart2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
    <h2 className="text-xl font-semibold text-foreground mb-2">{title}</h2>
    <p className="text-slate-600 max-w-md mx-auto">{body}</p>
  </div>
);

export default ReportBuilderPage;
